import React, { useRef } from "react";
import { FONTS, PLACEHOLDERS } from "@/lib/studio/templates";
import { fileToDataUrl } from "@/lib/studio/render";
import { toast } from "sonner";
import { Trash2, Copy, ArrowUp, ArrowDown, Upload } from "lucide-react";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="ccs-label text-[10px] block mb-1">{label}</span>
    {children}
  </label>
);

const Num = ({ value, onChange, step = 0.5, min, testId }) => (
  <input type="number" step={step} min={min} value={value ?? 0}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    className="ccs-input text-xs w-full" data-testid={testId} />
);

const Color = ({ value, onChange, testId }) => (
  <input type="color" value={value || "#111111"} onChange={(e) => onChange(e.target.value)}
    className="h-8 w-full cursor-pointer bg-transparent" data-testid={testId} />
);

export function ElementInspector({ el, onUpdate, onDelete, onDuplicate, onReorder }) {
  const fileRef = useRef(null);
  if (!el) {
    return (
      <div className="muted-text text-xs" data-testid="inspector-empty">
        Click an element on the page to edit it. Drag to move; drag the amber corner to resize.
        Arrow keys nudge, Delete removes.
      </div>
    );
  }
  const set = (patch) => onUpdate(el.id, patch);

  const pickImage = async (f) => {
    if (!f) return;
    try { set({ src: await fileToDataUrl(f) }); }
    catch { toast.error("Could not read that image"); }
  };

  return (
    <div className="space-y-3" data-testid="inspector">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium capitalize">{el.type}{el.label ? ` · ${el.label}` : ""}</span>
        <div className="flex gap-1">
          <button className="ccs-btn-ghost p-1" title="Send backward" onClick={() => onReorder(el.id, -1)} data-testid="inspector-back"><ArrowDown size={12} /></button>
          <button className="ccs-btn-ghost p-1" title="Bring forward" onClick={() => onReorder(el.id, 1)} data-testid="inspector-forward"><ArrowUp size={12} /></button>
          <button className="ccs-btn-ghost p-1" title="Duplicate" onClick={() => onDuplicate(el.id)} data-testid="inspector-duplicate"><Copy size={12} /></button>
          <button className="ccs-btn-danger p-1" title="Delete" onClick={() => onDelete(el.id)} data-testid="inspector-delete"><Trash2 size={12} /></button>
        </div>
      </div>

      {el.type === "text" && (
        <>
          <Field label="Text">
            <textarea value={el.text} onChange={(e) => set({ text: e.target.value })} rows={3}
              className="ccs-input text-xs w-full" data-testid="inspector-text" />
          </Field>
          <Field label="Insert field">
            <select className="ccs-input text-xs w-full" value="" data-testid="inspector-insert-placeholder"
              onChange={(e) => { if (e.target.value) set({ text: `${el.text || ""}{{${e.target.value}}}` }); }}>
              <option value="">Add a data field…</option>
              {PLACEHOLDERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Font">
              <select className="ccs-input text-xs w-full" value={el.font} onChange={(e) => set({ font: e.target.value })} data-testid="inspector-font">
                {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </Field>
            <Field label="Size (pt)"><Num value={el.fontSize} step={1} min={4} onChange={(v) => set({ fontSize: v })} testId="inspector-font-size" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-2 items-end">
            <Field label="Colour"><Color value={el.color} onChange={(v) => set({ color: v })} testId="inspector-color" /></Field>
            <button className={`text-xs py-1.5 border ${el.bold ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text"}`}
              onClick={() => set({ bold: !el.bold })} data-testid="inspector-bold"><b>B</b></button>
            <button className={`text-xs py-1.5 border ${el.italic ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text"}`}
              onClick={() => set({ italic: !el.italic })} data-testid="inspector-italic"><i>I</i></button>
          </div>
          <Field label="Align">
            <div className="grid grid-cols-3 gap-1">
              {["left", "center", "right"].map((a) => (
                <button key={a} onClick={() => set({ align: a })} data-testid={`inspector-align-${a}`}
                  className={`text-xs py-1 border capitalize ${el.align === a ? "border-amber-400 text-amber-400" : "border-neutral-700 muted-text"}`}>{a}</button>
              ))}
            </div>
          </Field>
        </>
      )}

      {el.type === "image" && (
        <>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickImage(e.target.files?.[0])} data-testid="inspector-image-file" />
          <div className="grid grid-cols-2 gap-2">
            <button className="ccs-btn-ghost text-xs flex items-center justify-center gap-1" onClick={() => fileRef.current?.click()} data-testid="inspector-image-upload">
              <Upload size={12} /> Upload
            </button>
            <button className="ccs-btn-ghost text-xs" onClick={() => set({ src: "club_logo" })} data-testid="inspector-image-club-logo">Use club logo</button>
            <button className="ccs-btn-ghost text-xs col-span-2" onClick={() => set({ src: "club_signature", label: el.label || "Signature" })} data-testid="inspector-image-club-signature">Use saved signature</button>
          </div>
          <Field label="Label"><input className="ccs-input text-xs w-full" value={el.label || ""} onChange={(e) => set({ label: e.target.value })} data-testid="inspector-image-label" /></Field>
        </>
      )}

      {(el.type === "line" || el.type === "rect") && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Colour"><Color value={el.color} onChange={(v) => set({ color: v })} testId="inspector-stroke-color" /></Field>
          <Field label="Thickness (mm)"><Num value={el.strokeWidth} step={0.1} min={0.1} onChange={(v) => set({ strokeWidth: v })} testId="inspector-stroke-width" /></Field>
          {el.type === "rect" && (
            <Field label="Fill">
              <div className="flex gap-1 items-center">
                <input type="color" value={el.fill || "#ffffff"} onChange={(e) => set({ fill: e.target.value })} className="h-8 w-full bg-transparent" data-testid="inspector-fill" />
                <button className="ccs-btn-ghost text-[10px] px-2" onClick={() => set({ fill: "" })} data-testid="inspector-fill-clear">None</button>
              </div>
            </Field>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Field label="X"><Num value={el.x} onChange={(v) => set({ x: v })} testId="inspector-x" /></Field>
        <Field label="Y"><Num value={el.y} onChange={(v) => set({ y: v })} testId="inspector-y" /></Field>
        <Field label="W"><Num value={el.w} min={1} onChange={(v) => set({ w: v })} testId="inspector-w" /></Field>
        {el.type !== "text" && el.type !== "line" && (
          <Field label="H"><Num value={el.h} min={1} onChange={(v) => set({ h: v })} testId="inspector-h" /></Field>
        )}
      </div>
    </div>
  );
}
